import React from "react";
import "./AboutSec1.css";
import { useLocation } from "react-router-dom";

export default function AboutSec1({ currentLanguage = "en" }) {
  const location = useLocation();

  // SHOW DATA ONLY WHEN PATH = /about
  if (location.pathname !== "/about") return null;

  const translations = {
    en: {
      left: {
        subtitle: "ABOUT OUR COMPANY",
        title: "TRANSFORMING COMPLEXITY INTO STRATEGIC ADVANTAGE",
        paragraphs: [
          "In a marketplace defined by rapid policy shifts, capital constraints, and rising risk exposure, Pestel & Partners serves as a strategic anchor for organizations seeking clarity and long-term resilience.",
          "Our methodology is rooted in evidence-based assessment, transparent methodologies, and a multidisciplinary lens. We combine financial analysis, market intelligence, regulatory insights, and operational benchmarking to help organizations make informed decisions with confidence.",
          "In today's data-driven world, precision and speed matter more than ever. We leverage advanced analytics, risk modelling tools, and digital diagnostic platforms to deliver sharper insights, uncover hidden opportunities, and mitigate emerging threats. This technology-enabled approach allows us to help businesses optimize capital allocation, strengthen internal controls, and achieve measurable performance improvements."
        ]
      },
      right: {
        paragraphs: [
          "At Pestel & Partners, our commitment extends beyond delivering reports or frameworks—we stay invested in the outcomes. Every engagement is guided by integrity, independent thinking, and a deep responsibility to our clients' financial well-being.",
          "Our promise is simple: consistent excellence, unwavering partnership, and value that endures."
        ],
        img: "/assets/images/AboutUsImg1.png"
      }
    },
    fr: {
      left: {
        subtitle: "À PROPOS DE NOTRE ENTREPRISE", 
        title: "TRANSFORMER LA COMPLEXITÉ EN AVANTAGE STRATÉGIQUE",
        paragraphs: [
          "Dans un marché défini par des changements politiques rapides, des contraintes de capital et une exposition croissante aux risques, Pestel & Partners sert d'ancrage stratégique pour les organisations en quête de clarté et de résilience à long terme.",
          "Notre méthodologie est ancrée dans l'évaluation fondée sur des preuves, des méthodologies transparentes et une perspective multidisciplinaire. Nous combinons l'analyse financière, l'intelligence du marché, les perspectives réglementaires et l'analyse comparative opérationnelle pour aider les organisations à prendre des décisions éclairées en toute confiance.",
          "Dans le monde actuel axé sur les données, la précision et la rapidité comptent plus que jamais. Nous exploitons des analyses avancées, des outils de modélisation des risques et des plateformes de diagnostic numérique pour fournir des informations plus précises, découvrir des opportunités cachées et atténuer les menaces émergentes. Cette approche technologique nous permet d'aider les entreprises à optimiser l'allocation du capital, à renforcer les contrôles internes et à réaliser des améliorations de performance mesurables."
        ]
      },
      right: {
        paragraphs: [
          "Chez Pestel & Partners, notre engagement va au-delà de la fourniture de rapports ou de cadres—nous restons investis dans les résultats. Chaque engagement est guidé par l'intégrité, la pensée indépendante et une profonde responsabilité envers le bien-être financier de nos clients.",
          "Notre promesse est simple : excellence constante, partenariat indéfectible et valeur durable."
        ],
        img: "/assets/images/AboutUsImg1.png"
      }
    },
    hi: {
      left: {
        subtitle: "हमारी कंपनी के बारे में",
        title: "जटिलता को रणनीतिक लाभ में बदलना",
        paragraphs: [
          "तेजी से नीतिगत बदलाव, पूंजी की कमी और बढ़ते जोखिम के संपर्क से परिभाषित बाजार में, पेस्टल एंड पार्टनर्स उन संगठनों के लिए एक रणनीतिक आधार के रूप में कार्य करता है जो स्पष्टता और दीर्घकालिक लचीलापन चाहते हैं।",
          "हमारी पद्धति साक्ष्य-आधारित मूल्यांकन, पारदर्शी तरीकों और बहु-विषयक दृष्टिकोण में निहित है। हम वित्तीय विश्लेषण, बाजार खुफिया, नियामक अंतर्दृष्टि और परिचालन बेंचमार्किंग को मिलाकर संगठनों को आत्मविश्वास के साथ सूचित निर्णय लेने में मदद करते हैं।",
          "आज की डेटा-संचालित दुनिया में, सटीकता और गति पहले से कहीं अधिक मायने रखती है। हम तेज अंतर्दृष्टि प्रदान करने, छिपे अवसरों को उजागर करने और उभरते खतरों को कम करने के लिए उन्नत विश्लेषण, जोखिम मॉडलिंग उपकरण और डिजिटल नैदानिक प्लेटफार्मों का लाभ उठाते हैं। यह प्रौद्योगिकी-सक्षम दृष्टिकोण हमें व्यवसायों को पूंजी आवंटन को अनुकूलित करने, आंतरिक नियंत्रण को मजबूत करने और मापने योग्य प्रदर्शन सुधार हासिल करने में मदद करने की अनुमति देता है।"
        ]
      },
      right: {
        paragraphs: [
          "पेस्टल एंड पार्टनर्स में, हमारी प्रतिबद्धता रिपोर्ट या ढांचे प्रदान करने से परे है—हम परिणामों में निवेशित रहते हैं। प्रत्येक जुड़ाव अखंडता, स्वतंत्र सोच और हमारे ग्राहकों की वित्तीय भलाई के प्रति गहरी जिम्मेदारी द्वारा निर्देशित होता है।",
          "हमारा वादा सरल है: निरंतर उत्कृष्टता, अटूट साझेदारी और स्थायी मूल्य।"
        ],
        img: "/assets/images/AboutUsImg1.png"
      }
    }
  };

  const aboutSec1Data = translations[currentLanguage] || translations.en;

  return (
    <section className="aboutsec1-main section">
      <div className="aboutsec1-grid">
        {/* LEFT CONTENT */}
        <div className="aboutsec1-left">
          <div className="d-flex align-items-center mb-2">
            <div className="about-line me-3"></div>
            <p className="service-subtitle ms-2 mb-0">
              {aboutSec1Data.left.subtitle}
            </p>
          </div>

          <h2 className="aboutsec1-title">
            {aboutSec1Data.left.title}
          </h2>

          {aboutSec1Data.left.paragraphs.map((text, index) => (
            <p className="aboutsec1-text" key={index}>
              {text}
            </p>
          ))}
        </div>

        {/* RIGHT CONTENT */}
        <div className="aboutsec1-right">
          {aboutSec1Data.right.paragraphs.map((text, index) => (
            <p className="aboutsec1-text" key={index}>
              {text}
            </p>
          ))}

          <img
            src={aboutSec1Data.right.img}
            alt="About Company"
            className="aboutsec1-img"
          />
        </div>
      </div>
    </section>
  );
}